import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Slide {
  tag: string;
  title: string;
  desc: string;
  image: string;
  path: string;
}

const slides: Slide[] = [
  {
    tag: "焦点 · 活动报道",
    title: "2026 夏日音乐节阵容官宣，三十组艺人集结开唱",
    desc: "四大主题舞台、创意市集与美食街区，打造一站式的夏日娱乐体验。",
    image:
      "https://readdy.ai/api/search-image?query=stylized%20wide%20concert%20stage%20with%20warm%20orange%20spotlights%20and%20large%20crowd%20silhouettes%2C%20artistic%20cinematic%20poster%20illustration%2C%20vibrant%20energetic%20summer%20mood&width=1600&height=900&seq=hero-01&orientation=landscape",
    path: "/news/detail/summer-music-festival-2026",
  },
  {
    tag: "焦点 · 影视推荐",
    title: "回声之城：一场关于记忆与人性的科幻旅程",
    desc: "在记忆被记录的未来城市里，一段被抹除的片段，牵出一场追寻真相的旅程。",
    image:
      "https://readdy.ai/api/search-image?query=wide%20futuristic%20city%20skyline%20with%20glowing%20orange%20neon%20lines%20under%20a%20dramatic%20dusk%20sky%2C%20cinematic%20sci%20fi%20illustration%2C%20moody%20atmosphere&width=1600&height=900&seq=hero-02&orientation=landscape",
    path: "/movies/detail/city-of-echoes",
  },
  {
    tag: "焦点 · 行业动态",
    title: "国产科幻新片首周末票房破六亿，口碑持续走高",
    desc: "扎实特效与动人故事并行，成为今年暑期档最亮眼的黑马，排片持续增加。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20wide%20cinema%20screening%20room%20with%20glowing%20screen%20and%20warm%20amber%20light%2C%20artistic%20editorial%20illustration%2C%20premium%20filmic%20tone&width=1600&height=900&seq=hero-03&orientation=landscape",
    path: "/news/detail/new-sci-fi-film-box-office",
  },
  {
    tag: "焦点 · 社区活动",
    title: "夏日音乐节现场攻略：和社区伙伴一起去看演出",
    desc: "来自社区的真实体验与避坑指南，找到你的观演搭子，让这个夏天更热闹。",
    image:
      "https://readdy.ai/api/search-image?query=wide%20outdoor%20summer%20festival%20crowd%20with%20warm%20orange%20lights%20and%20banners%20at%20dusk%2C%20artistic%20illustration%2C%20joyful%20energetic%20mood&width=1600&height=900&seq=hero-04&orientation=landscape",
    path: "/community/detail/summer-festival-tips",
  },
];

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const pausedRef = useRef(false);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setActive((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!pausedRef.current) next();
    }, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden md:h-[520px] lg:h-[560px]"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
      aria-roledescription="轮播"
      aria-label="焦点内容"
    >
      {slides.map((slide, idx) => (
        <div
          key={slide.title}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={idx !== active}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 色花堂焦点`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/50 to-secondary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 flex h-full items-center px-4 md:px-8">
        <div className="w-full max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill" aria-hidden="true"></i>
            {slides[active].tag}
          </span>
          <h1 key={slides[active].title} className="animate-fade-up mt-4 font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl lg:text-[2.75rem]">
            {slides[active].title}
          </h1>
          <p className="animate-fade-up mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {slides[active].desc}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(slides[active].path)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看详情
              <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => navigate("/news")}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
            >
              浏览更多资讯
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 transition-colors hover:bg-secondary-950/70 md:flex cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 transition-colors hover:bg-secondary-950/70 md:flex cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, idx) => (
          <button
            key={slide.title}
            type="button"
            onClick={() => setActive(idx)}
            aria-label={`切换到第 ${idx + 1} 张`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              idx === active ? "w-8 bg-primary-500" : "w-2 bg-background-50/60 hover:bg-background-50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}